import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";

import type { Capability, Session } from "@/api/hooks";

import type { AuthAdapter } from "./types";

export interface SessionContextValue {
  session: Session;
  adapter: AuthAdapter;
  /**
   * True only when the capability was explicitly granted by the service.
   * Unknown or absent capabilities are denied — the browser mirrors the
   * server's decision and never widens it.
   */
  can: (capability: Capability) => boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({
  session,
  adapter,
  children,
}: {
  session: Session;
  adapter: AuthAdapter;
  children: ReactNode;
}) {
  const granted = useMemo(
    () => new Set<string>(session.capabilities),
    [session.capabilities],
  );

  const can = useCallback(
    (capability: Capability) => granted.has(capability),
    [granted],
  );

  const value = useMemo<SessionContextValue>(
    () => ({ session, adapter, can }),
    [session, adapter, can],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const value = useOptionalSession();
  if (!value) {
    throw new Error(
      "useSession was called outside SessionProvider. The application shell " +
        "must not render before the session is resolved.",
    );
  }
  return value;
}

/**
 * The session if one has been resolved, `null` before that.
 *
 * For chrome that may be on screen while identity is still unknown. Such a
 * surface must render nothing where the session's value would go — never a
 * placeholder standing in for an identity nobody has established yet.
 */
export function useOptionalSession(): SessionContextValue | null {
  return useContext(SessionContext);
}

/**
 * Capability gate for a single control.
 *
 * Returns whether the action is permitted and, when it is not, the reason to
 * put on the disabled control. ADR 0001 requires read-only and unauthorized
 * content to disable controls rather than merely badge them, and a disabled
 * control with no explanation is its own usability defect.
 */
export function useCapability(capability: Capability): {
  allowed: boolean;
  reason: string | null;
} {
  const { can } = useSession();
  const allowed = can(capability);
  return {
    allowed,
    reason: allowed
      ? null
      : `Your account does not have the "${capability}" permission.`,
  };
}
